/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2025 HG Digital/Setera
 * Todos os direitos reservados.
 * 
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 26-06-2025
 * Autor     : diegobrock
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import { Component, Inject, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Subscription, interval } from 'rxjs';
import { take } from 'rxjs/operators';
import { SRSStreamingService, SRSStreamInfo, SRSConnectionStatus } from '../../../services/srs-streaming.service';
import { StreamingSessionService } from '../../../services/streaming-session.service';
import { IStreamingSession, CreateStreamingSessionRequest, StreamingSessionStatus } from '../../../models/streaming-session.model';
import { CommandSenderComponent } from '../terminal/command-sender.component';
import { CurrentPosition } from '../../../models/CurrentPosition';

@Component({
  selector: 'app-video-stream-player',
  templateUrl: './video-stream-player.component.html',
  styleUrls: ['./video-stream-player.component.scss']
})
export class VideoStreamPlayerComponent implements OnInit, OnDestroy {
  isLoading = true;
  errorMessage: string | null = null;
  connectionStatus = 'Conectando...';
  playerReady = false;
  selectedProtocol: 'flv' = 'flv';
  currentStreamInfo: SRSStreamInfo | null = null;
  
  // Novas propriedades para sessões
  currentSession: IStreamingSession | null = null;
  sessionLoading = false;
  sessionError: string | null = null;

  // Propriedade para CurrentPosition
  currentPosition: CurrentPosition | null = null;

  // Propriedades para o contador decrescente
  timeRemainingSeconds: number = 0;
  private countdownTimer: Subscription | null = null;
  private readonly SESSION_DURATION_MINUTES = 1; // 1 minuto de duração da sessão
  private playbackStarted = false; // Flag para controlar quando iniciar o contador
  private playbackStartTime: Date | null = null; // Momento em que a reprodução iniciou

  private subscriptions: Subscription[] = [];
  private videoElement: HTMLVideoElement | null = null;
  private static flvjs: any = null;
  private flvPlayer: any = null;

  @ViewChild('videoPlayer', { static: true }) videoPlayer!: ElementRef<HTMLVideoElement>;

  private pollingAttempts = 0;
  private pollingMaxAttempts = 15;
  private pollingIntervalMs = 2000;

  private createdSessionUuid: string | null = null;
  private stopCommandSent = false; // Flag para evitar envio múltiplo do comando de parada
  private beforeUnloadHandler: (event: BeforeUnloadEvent) => void; // Handler para fechamento da janela
  private visibilityChangeHandler: () => void; // Handler para mudança de visibilidade (fallback)

  constructor(
    public dialogRef: MatDialogRef<VideoStreamPlayerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { 
      currentPosition?: CurrentPosition; // Novo campo para CurrentPosition
      simCard: string; 
      terminalId?: number; 
      userId?: number;
      autoCreateSession?: boolean;
      streamingSessionUuid?: string;
    },
    private srsStreamingService: SRSStreamingService,
    private streamingSessionService: StreamingSessionService,
    private dialog: MatDialog
  ) {
    // Inicializa currentPosition com o valor recebido
    this.currentPosition = data.currentPosition || null;
    
    // Adiciona listener para quando o modal for fechado (ESC, clique fora, etc.)
    this.dialogRef.afterClosed().subscribe(() => {
      console.info('[VideoStreamPlayer] Modal fechado, enviando comando de parada de streaming');
      this.sendStopStreamingCommand();
    });
  }

  ngOnInit(): void {
    console.info('[VideoStreamPlayer] Inicializando componente com dados:', this.data);
    
    // Log das informações do CurrentPosition se disponível
    if (this.currentPosition) {
      console.info('[VideoStreamPlayer] CurrentPosition recebido:', {
        id: this.currentPosition.id,
        id_terminal: this.currentPosition.id_terminal,
        placa: this.currentPosition.placa,
        frota: this.currentPosition.frota,
        localizacao: this.currentPosition.localizacao,
        nome_motorista: this.currentPosition.nome_motorista,
        nome_empresa: this.currentPosition.nome_empresa,
        nome_divisao: this.currentPosition.nome_divisao,
        latitude: this.currentPosition.latitude,
        longitude: this.currentPosition.longitude
      });
    } else {
      console.warn('[VideoStreamPlayer] CurrentPosition não foi fornecido');
    }
    
    // Verifica se streamingSessionUuid está definido
    if (!this.data.streamingSessionUuid) {
      const errorMsg = 'UUID da sessão de streaming não foi fornecido. É obrigatório para iniciar o player.';
      console.error('[VideoStreamPlayer]', errorMsg);
      this.errorMessage = errorMsg;
      this.isLoading = false;
      this.sessionLoading = false;
      return;
    }


    // Se receber o uuid da sessão, faz polling apenas por ele
    console.info('[VideoStreamPlayer] Polling apenas por UUID:', this.data.streamingSessionUuid);
    this.createdSessionUuid = this.data.streamingSessionUuid;
    this.startPollingSessionByUuid(this.createdSessionUuid);

    // Adiciona listener para fechamento da janela/aba do navegador
    this.beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      console.info('[VideoStreamPlayer] Janela sendo fechada, enviando comando de parada de streaming');
      this.sendStopStreamingCommand();
    };
    
    // Adiciona listener para mudança de visibilidade como fallback
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'hidden') {
        console.info('[VideoStreamPlayer] Página oculta, enviando comando de parada de streaming (fallback)');
        this.sendStopStreamingCommand();
      }
    };
    
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Obtém o canal lógico da sessão atual ou usa valor padrão
   */
  private getLogicalChannel(): number {
    const logicalChannel = this.currentSession?.logicalChannel;
    if (logicalChannel !== undefined && logicalChannel !== null) {
      console.info('[VideoStreamPlayer] Canal lógico obtido da sessão:', logicalChannel);
      return logicalChannel;
    }
    console.warn('[VideoStreamPlayer] Canal lógico não encontrado na sessão, usando valor padrão: 1');
    return 1; // Fallback para valor padrão
  }

  /**
   * Obtém a descrição do canal lógico baseado no número
   */
  public getLogicalChannelDescription(channelNumber: number): string {
    const channelDescriptions: { [key: number]: string } = {
      1: 'Câmera Principal',
      2: 'Câmera Interna',
      3: 'Câmera Auxiliar 1',
      4: 'Câmera Auxiliar 2'
    };

    const description = channelDescriptions[channelNumber] || `Canal ${channelNumber}`;
    console.info(`[VideoStreamPlayer] Canal lógico ${channelNumber} mapeado para: ${description}`);
    return description;
  }

  // Novo método para garantir polling assíncrono
  private startPollingSessionByUuid(sessionUuid: string) {
    (async () => {
      await this.pollSessionByUuidUntilActive(sessionUuid);
    })();
  }

  /**
   * Polling para aguardar a sessão criada ficar ativa
   */
  private async pollSessionByUuidUntilActive(sessionUuid: string, maxAttempts = 15, intervalMs = 2000): Promise<void> {
    this.pollingAttempts = 0;
    this.sessionLoading = true;
    this.sessionError = null;
    this.currentSession = null;

    let foundActive = false;
    while (this.pollingAttempts < maxAttempts && !foundActive) {
      this.pollingAttempts++;
      try {
        console.info(`[VideoStreamPlayer][Polling] Tentativa ${this.pollingAttempts} para UUID:`, sessionUuid);
        const session = await this.streamingSessionService.getStreamingSession(sessionUuid).pipe(take(1)).toPromise();

        if (session.status === StreamingSessionStatus.ACTIVE && !session.isExpired) {
          this.currentSession = session;

          console.info('[VideoStreamPlayer] Sessão ativa encontrada:', session.uuid);
          foundActive = true;
          break;
        }
      } catch (error) {
        console.error('[VideoStreamPlayer][Polling] Erro ao buscar sessão por UUID:', error);
      }
      this.connectionStatus = `Aguardando ativação da sessão... (${this.pollingAttempts}/${maxAttempts})`;
      await new Promise(res => setTimeout(res, intervalMs));
    }
    if (foundActive) {
      this.sessionLoading = false;
      this.initializeStreamingWithSession();
    } else {
      this.sessionLoading = false;
      this.isLoading = false;
      this.errorMessage = 'A sessão criada não ficou ativa após várias tentativas.';
    }
  }

  /**
   * Aguarda o stream ficar ativo usando UUID da sessão
   */
  private async waitForStreamActiveWithSession(sessionUuid: string, maxAttempts = 15, intervalMs = 2000): Promise<boolean> {
    // Se temos uma sessão com streamUrl da API, usamos ela
    if (this.currentSession?.streamUrl) {
      console.info('[VideoStreamPlayer] Usando streamUrl da API para verificação:', this.currentSession.streamUrl);
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const isActive = await this.srsStreamingService.checkStreamStatusWithUrl(this.currentSession.streamUrl!);
        if (isActive) return true;
        this.connectionStatus = `Aguardando início do streaming... (${attempt}/${maxAttempts}) - URL da API`;
        await new Promise(res => setTimeout(res, intervalMs));
      }
    } else {
      // Fallback para verificação local usando UUID
      console.info('[VideoStreamPlayer] Usando verificação local com UUID da sessão');
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const isActive = await this.srsStreamingService.checkStreamStatusWithSession(sessionUuid);
        if (isActive) return true;
        this.connectionStatus = `Aguardando início do streaming... (${attempt}/${maxAttempts}) - UUID local`;
        await new Promise(res => setTimeout(res, intervalMs));
      }
    }
    return false;
  }

  /**
   * Inicializa o streaming com uma sessão existente
   */
  private async initializeStreamingWithSession(): Promise<void> {
    if (!this.currentSession) {
      this.errorMessage = 'Sessão de streaming não encontrada.';
      this.isLoading = false;
      return;
    }

    try {
      this.currentStreamInfo = this.generateSecureStreamUrls()[0];
      // Polling para aguardar stream ativo usando UUID da sessão
      const isStreamActive = await this.waitForStreamActiveWithSession(this.currentSession.uuid);
      if (!isStreamActive) {
        this.errorMessage = 'Stream não foi iniciado pelo terminal. Tente novamente em instantes.';
        this.isLoading = false;
        return;
      }
      await this.startStreamingWithSession();

      // Inscreve nos observables do serviço
      this.subscriptions.push(
        this.srsStreamingService.connectionStatus$.subscribe(status => {
          this.updateConnectionStatus(status);
        }),
        
        this.srsStreamingService.streamInfo$.subscribe(streamInfo => {
          this.currentStreamInfo = streamInfo;
          if (streamInfo && streamInfo.isActive) {
            this.playerReady = true;
            this.isLoading = false;
          }
        })
      );

    } catch (error) {
      this.errorMessage = `Erro ao inicializar streaming: ${error instanceof Error ? error.message : 'Erro desconhecido'}`;
      this.isLoading = false;
      console.error('Erro na inicialização:', error);
    }
  }

  /**
   * Gera URLs de streaming seguras com a sessão
   */
  private generateSecureStreamUrls(): SRSStreamInfo[] {
    if (!this.currentSession) return [];

    const streamUrl = this.streamingSessionService.generateSecureStreamUrl(this.currentSession, 'flv');
    
    console.info('[VideoStreamPlayer] Stream URL:', {
      sessionUuid: this.currentSession.uuid,
      streamUrl: streamUrl,
      hasStreamUrlFromApi: !!this.currentSession.streamUrl,
      streamUrlFromApi: this.currentSession.streamUrl
    });

    return [
      {
        simCard: this.data.simCard,
        streamName: this.currentSession.uuid,
        protocol: 'flv',
        url: streamUrl,
        isActive: false
      }
    ];
  }

  /**
   * Inicia streaming com sessão
   */
  private async startStreamingWithSession(): Promise<void> {
    if (!this.currentSession) return;

    try {
      this.isLoading = true;
      this.errorMessage = null;

      this.currentStreamInfo = this.generateSecureStreamUrls()[0];
      await this.setupFLVPlayer();

    } catch (error) {
      this.errorMessage = `Erro ao iniciar streaming: ${error instanceof Error ? error.message : 'Erro desconhecido'}`;
      this.isLoading = false;
      console.error('Erro no streaming:', error);
    }
  }

  private async setupFLVPlayer(): Promise<void> {
    if (!this.currentStreamInfo) return;

    // Importa flv.js dinamicamente se necessário
    if (!VideoStreamPlayerComponent.flvjs) {
      VideoStreamPlayerComponent.flvjs = (await import('flv.js')).default;
    }
    const flvjs = VideoStreamPlayerComponent.flvjs;

    this.videoElement = this.videoPlayer.nativeElement;

    // Destroi player anterior se existir
    if (this.flvPlayer) {
      this.flvPlayer.destroy();
      this.flvPlayer = null;
    }

    if (flvjs.isSupported()) {
      console.info('Criando FLV.js player com URL:', this.currentStreamInfo.url);
      
      // Configurações básicas do elemento de vídeo
      this.videoElement.muted = true; // Muta o vídeo para permitir autoplay
      this.videoElement.autoplay = true; // Habilita autoplay
      //this.videoElement.playsInline = true; // Força reprodução inline
      this.videoElement.controls = true; // Habilita controles nativos
      
      // Garante que o elemento de vídeo esteja visível
      this.videoElement.style.display = 'block';
      this.videoElement.style.visibility = 'visible';
      this.videoElement.style.opacity = '1';
      this.videoElement.style.width = '100%';
      this.videoElement.style.height = '100%';
      
      console.info('Configurações do elemento de vídeo:', {
        muted: this.videoElement.muted,
        autoplay: this.videoElement.autoplay,
        //playsInline: this.videoElement.playsInline,
        controls: this.videoElement.controls,
        display: this.videoElement.style.display,
        visibility: this.videoElement.style.visibility,
        opacity: this.videoElement.style.opacity
      });
      
      this.flvPlayer = flvjs.createPlayer({
        type: 'flv',
        url: this.currentStreamInfo.url,
        isLive: true,
        hasAudio: false,
        hasVideo: true,
        enableStashBuffer: false,
        stashInitialSize: 256,
        enableWorker: true,
        lazyLoad: false,
        autoCleanupSourceBuffer: true,
        fixAudioTimestampGap: false,
        accurateSeek: false,
        seekType: 'range',
        rangeLoadZeroStart: false,
        customSeekHandler: null,
        customLoader: null,
        cors: true,
        // Configurações específicas para streaming ao vivo
        liveBufferLatencyChasing: true,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 10,
        liveDurationInfinity: true,
        liveFlvPlayer: true
      });

      console.info('FLV.js player criado ►', this.flvPlayer);
      
      // Anexa o player ao elemento de vídeo
      this.flvPlayer.attachMediaElement(this.videoElement);
      console.info('FLV.js player anexado ao video element');
      
      // Event listener para quando a reprodução iniciar
      this.videoElement.addEventListener('playing', () => {
        if (!this.playbackStarted) {
          this.playbackStarted = true;
          this.playbackStartTime = new Date();
          this.startCountdownTimer();
          console.info('[VideoStreamPlayer] Reprodução iniciada - contador ativado às', this.playbackStartTime.toLocaleTimeString());
        }
      });
      
      // Carrega o stream
      this.flvPlayer.load();
      console.info('FLV.js player load chamado');

      // Event listeners do FLV.js
      this.flvPlayer.on(flvjs.Events.ERROR, (errType: any, errDetail: any, errInfo: any) => {
        if (errDetail === 'UnrecoverableEarlyEof') {
          this.errorMessage = 'Streaming encerrado pelo terminal.';
        } else {
          this.errorMessage = `Erro FLV.js: ${errType} - ${errDetail}`;
        }
        
        console.error('Erro FLV.js:', errType, errDetail, errInfo);
      });

      this.flvPlayer.on(flvjs.Events.LOADING_COMPLETE, () => {
        this.playerReady = true;
        this.isLoading = false;
        this.connectionStatus = `FLV conectado: ${this.currentStreamInfo?.streamName}`;
        console.info('FLV.js LOADING_COMPLETE');
        
        // Tenta reproduzir quando o carregamento estiver completo
        this.attemptVideoPlay();
      });

      this.flvPlayer.on(flvjs.Events.MEDIA_INFO, (info: any) => {
        console.info('FLV.js MEDIA_INFO:', info);
        // Tenta reproduzir quando receber informações de mídia
        setTimeout(() => this.attemptVideoPlay(), 100);
      });

      this.flvPlayer.on(flvjs.Events.METADATA_ARRIVED, (metadata: any) => {
        console.info('FLV.js METADATA_ARRIVED:', metadata);
      });

      this.flvPlayer.on(flvjs.Events.STATISTICS_INFO, (stats: any) => {
        console.info('FLV.js STATISTICS_INFO:', stats);
      });

      this.flvPlayer.on(flvjs.Events.SCRIPTDATA_ARRIVED, (data: any) => {
        console.info('FLV.js SCRIPTDATA_ARRIVED:', data);
      });

      this.flvPlayer.on(flvjs.Events.VIDEO_ARRIVED, (data: any) => {
        console.info('FLV.js VIDEO_ARRIVED:', data);
        // Tenta reproduzir quando receber dados de vídeo
        setTimeout(() => this.attemptVideoPlay(), 50);
      });

      // Event listeners do elemento de vídeo
      this.videoElement.addEventListener('loadedmetadata', () => {
        console.info('Elemento de vídeo: loadedmetadata', {
          videoWidth: this.videoElement!.videoWidth,
          videoHeight: this.videoElement!.videoHeight,
          duration: this.videoElement!.duration,
          readyState: this.videoElement!.readyState
        });
      });

      this.videoElement.addEventListener('canplay', () => {
        console.info('Elemento de vídeo: canplay');
        this.attemptVideoPlay();
      });

      this.videoElement.addEventListener('playing', () => {
        console.info('Elemento de vídeo: playing');
        this.playerReady = true;
        this.isLoading = false;
      });

      this.videoElement.addEventListener('waiting', () => {
        console.info('Elemento de vídeo: waiting');
      });

      this.videoElement.addEventListener('stalled', () => {
        console.info('Elemento de vídeo: stalled');
      });

      this.videoElement.addEventListener('error', (event) => {
        console.error('Elemento de vídeo: error', event);
        this.errorMessage = 'Erro no elemento de vídeo';
        this.isLoading = false;
      });

      // Tenta reproduzir imediatamente
      setTimeout(() => this.attemptVideoPlay(), 500);

    } else {
      this.errorMessage = 'FLV.js não é suportado neste navegador.';
      this.isLoading = false;
    }
  }

  /**
   * Tenta reproduzir o vídeo
   */
  private async attemptVideoPlay(): Promise<void> {
    if (!this.videoElement) {
      console.warn('Elemento de vídeo não encontrado');
      return;
    }

    try {
      console.info('Tentando reproduzir vídeo...', {
        readyState: this.videoElement.readyState,
        networkState: this.videoElement.networkState,
        paused: this.videoElement.paused,
        currentTime: this.videoElement.currentTime,
        duration: this.videoElement.duration,
        videoWidth: this.videoElement.videoWidth,
        videoHeight: this.videoElement.videoHeight
      });
      
      // Verifica se o elemento está pronto
      if (this.videoElement.readyState >= 2) { // HAVE_CURRENT_DATA
        console.info('Elemento de vídeo pronto, tentando reproduzir...');
        
        // Força o elemento a ser visível
        this.videoElement.style.display = 'block';
        this.videoElement.style.visibility = 'visible';
        this.videoElement.style.opacity = '1';
        
        await this.videoElement.play();
        console.info('Vídeo reproduzido com sucesso');
        
        // Se chegou aqui, remove o muted para permitir áudio
        this.videoElement.muted = false;
        
        // Verifica se realmente está reproduzindo
        setTimeout(() => {
          console.info('Status após reprodução:', {
            paused: this.videoElement!.paused,
            currentTime: this.videoElement!.currentTime,
            readyState: this.videoElement!.readyState
          });
        }, 1000);
        
      } else {
        console.info('Elemento de vídeo ainda não está pronto, aguardando...', {
          readyState: this.videoElement.readyState,
          networkState: this.videoElement.networkState
        });
        // Tenta novamente após um delay
        setTimeout(() => this.attemptVideoPlay(), 200);
      }
      
    } catch (error) {
      console.warn('Erro ao reproduzir vídeo:', error);
      
      // Trata erros específicos de autoplay
      if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
        console.info('Autoplay bloqueado pelo navegador. Aguardando interação do usuário...');
        
        // Adiciona listener para interação do usuário
        const handleUserInteraction = async () => {
          try {
            await this.videoElement!.play();
            console.info('Vídeo reproduzido após interação do usuário');
            this.videoElement!.muted = false;
            
            // Remove os listeners após sucesso
            document.removeEventListener('click', handleUserInteraction);
            document.removeEventListener('keydown', handleUserInteraction);
            document.removeEventListener('touchstart', handleUserInteraction);
          } catch (playError) {
            console.error('Erro ao reproduzir após interação:', playError);
          }
        };

        // Adiciona listeners para diferentes tipos de interação
        document.addEventListener('click', handleUserInteraction, { once: true });
        document.addEventListener('keydown', handleUserInteraction, { once: true });
        document.addEventListener('touchstart', handleUserInteraction, { once: true });
      }
    }
  }

  private updateConnectionStatus(status: SRSConnectionStatus): void {
    if (status.connected) {
      this.connectionStatus = `${status.protocol.toUpperCase()} conectado: ${status.streamName}`;
      this.errorMessage = null;
    } else {
      this.connectionStatus = 'Desconectado';
      if (status.error) {
        this.errorMessage = `Erro de conexão: ${status.error}`;
      }
    }
  }

  public async refreshStream(): Promise<void> {
    this.playerReady = false;
    this.isLoading = true;
    this.errorMessage = null;

    try {
      if (this.currentSession) {
        await this.initializeStreamingWithSession();
      } else {
        // Não há mais initializeStreaming, apenas aguarde ou trate erro
        this.errorMessage = 'Nenhuma sessão ativa para atualizar o stream.';
        this.isLoading = false;
      }
    } catch (error) {
      this.errorMessage = `Erro ao atualizar stream: ${error instanceof Error ? error.message : 'Erro desconhecido'}`;
      this.isLoading = false;
    }
  }
  /**
   * Envia comando de finalização de streaming para o terminal
   */
  private sendStopStreamingCommand(): void {
    if (this.stopCommandSent) {
      console.info('[VideoStreamPlayer] Comando de parada já foi enviado, ignorando');
      return;
    }

    if (!this.data.terminalId) {
      console.warn('TerminalId não informado. Não é possível enviar comando de finalização.');
      return;
    }

    // Obtém o canal lógico da sessão atual
    const logicalChannel = this.getLogicalChannel();
    if (!logicalChannel) {
      console.warn('Canal lógico não disponível. Não é possível enviar comando de finalização.');
      return;
    }

    // Configurações customizadas para o comando de finalização
    const config = {
      dialogTitle: 'Finalizar Streaming',
      submitButtonLabel: 'Finalizar'
    };

    const dialogRef = this.dialog.open(CommandSenderComponent, {
      width: '600px',
      data: {
        terminalId: this.data.terminalId.toString(),
        commandAction: 'LIVE_VIDEO_CONTROL',
        streamingSessionUuid: this.currentSession?.uuid,
        mode: 'DIRECT',
        config: config
      }
    });

    // Executa o comando diretamente com os parâmetros especificados
    // Usa o canal lógico como primeiro parâmetro
    const logicalChannelStr = logicalChannel.toString();
    dialogRef.componentInstance.executeCommandDirectly('LIVE_VIDEO_CONTROL', [logicalChannelStr, '0', '0', '0']).subscribe({
      next: (result) => {
        console.log('Comando de finalização de streaming enviado com sucesso:', result);
        this.stopCommandSent = true; // Marca que o comando foi enviado com sucesso
      },
      error: (error) => {
        console.error('Erro ao enviar comando de finalização de streaming:', error);
        this.stopCommandSent = true; // Marca mesmo em caso de erro para evitar tentativas repetidas
      }
    });

    // Fecha o dialog automaticamente após o comando
    dialogRef.close();
  }

  /**
   * Envia comando LIVE_VIDEO_CONTROL com parâmetros customizados
   */
  private sendLiveVideoControlCommand(params: [string, string, string, string]): void {
    if (!this.data.terminalId) {
      console.warn('TerminalId não informado. Não é possível enviar comando.');
      return;
    }

    // Obtém o canal lógico da sessão atual
    const logicalChannel = this.getLogicalChannel();
    if (!logicalChannel) {
      console.warn('Canal lógico não disponível. Não é possível enviar comando.');
      return;
    }

    const dialogRef = this.dialog.open(CommandSenderComponent, {
      width: '600px',
      data: {
        terminalId: this.data.terminalId.toString(),
        commandAction: 'LIVE_VIDEO_CONTROL',
        streamingSessionUuid: this.currentSession?.uuid
      }
    });

    // Usa o canal lógico como primeiro parâmetro e os demais parâmetros fornecidos
    const logicalChannelStr = logicalChannel.toString();
    const commandParams = [logicalChannelStr, params[1], params[2], params[3]];
    
    dialogRef.componentInstance.executeCommandDirectly('LIVE_VIDEO_CONTROL', commandParams).subscribe({
      next: (result) => {
        console.log('Comando LIVE_VIDEO_CONTROL enviado:', commandParams, result);
      },
      error: (error) => {
        console.error('Erro ao enviar comando LIVE_VIDEO_CONTROL:', commandParams, error);
      }
    });
    dialogRef.close();
  }

  // Métodos para cada controle da tabela
  // O primeiro parâmetro (canal lógico) será inserido automaticamente pelo sendLiveVideoControlCommand
  public controlTurnOffAudioVideo() {
    this.sendLiveVideoControlCommand(['0', '0', '0', '0']);
  }
  public controlSwitchStreamPrimary() {
    this.sendLiveVideoControlCommand(['0', '1', '0', '0']);
  }
  public controlSwitchStreamSub() {
    this.sendLiveVideoControlCommand(['0', '1', '0', '1']);
  }
  public controlPauseAllStreams() {
    this.sendLiveVideoControlCommand(['0', '2', '0', '0']);
  }
  public controlResumeStream() {
    this.sendLiveVideoControlCommand(['0', '3', '0', '0']);
  }
  public controlTurnOffIntercom() {
    this.sendLiveVideoControlCommand(['0', '4', '0', '0']);
  }
  public controlTurnOffOnlyAudio() {
    this.sendLiveVideoControlCommand(['0', '0', '1', '0']);
  }
  public controlTurnOffOnlyVideo() {
    this.sendLiveVideoControlCommand(['0', '0', '2', '0']);
  }

  close(): void {
    // Envia comando de finalização para o terminal
    //this.sendStopStreamingCommand();
    
    // Cancela a sessão antes de fechar
    //this.cancelSession();
    this.dialogRef.close();
  }

  /**
   * Inicia o contador decrescente baseado no momento em que a reprodução iniciou
   */
  private startCountdownTimer(): void {
    if (!this.playbackStartTime) {
      console.warn('[VideoStreamPlayer] Não foi possível iniciar o contador: playbackStartTime não disponível');
      return;
    }

    // Para o timer anterior se existir
    this.stopCountdownTimer();

    // Calcula o tempo restante inicial
    this.calculateTimeRemaining();

    // Inicia o timer que atualiza a cada segundo
    this.countdownTimer = interval(1000).subscribe(() => {
      this.calculateTimeRemaining();
    });

    console.log('[VideoStreamPlayer] Contador decrescente iniciado a partir de', this.playbackStartTime.toLocaleTimeString());
  }

  /**
   * Para o contador decrescente
   */
  private stopCountdownTimer(): void {
    if (this.countdownTimer) {
      this.countdownTimer.unsubscribe();
      this.countdownTimer = null;
      console.log('[VideoStreamPlayer] Contador decrescente parado');
    }
  }

  /**
   * Calcula o tempo restante baseado no momento em que a reprodução iniciou
   */
  private calculateTimeRemaining(): void {
    if (!this.playbackStartTime) {
      this.timeRemainingSeconds = 0;
      return;
    }

    const startTime = this.playbackStartTime.getTime();
    const currentTime = new Date().getTime();
    const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
    const totalDurationSeconds = this.SESSION_DURATION_MINUTES * 60;
    
    this.timeRemainingSeconds = Math.max(0, totalDurationSeconds - elapsedSeconds);

    // Se o tempo expirou, executa ação automática
    if (this.timeRemainingSeconds <= 0) {
      this.stopCountdownTimer();
      console.log('[VideoStreamPlayer] Tempo da sessão expirado - pausando player e exibindo mensagem');
      
      // Pausa o player de vídeo
      this.pauseVideoPlayer();
      
      // Exibe mensagem para o usuário
      this.errorMessage = 'Tempo de vídeo ao vivo contínuo esgotado. Se precisar, inicie uma nova transmissão.';
      
      // Envia comando de finalização (mantém a lógica existente)
      this.sendStopStreamingCommand();
    }
  }

  /**
   * Pausa o player de vídeo
   */
  private pauseVideoPlayer(): void {
    try {
      if (this.videoElement && !this.videoElement.paused) {
        this.videoElement.pause();
        console.log('[VideoStreamPlayer] Player de vídeo pausado devido ao tempo expirado');
      }
      
      if (this.flvPlayer) {
        // Pausa o player FLV.js se necessário
        this.flvPlayer.pause();
        console.log('[VideoStreamPlayer] Player FLV.js pausado devido ao tempo expirado');
      }
    } catch (error) {
      console.warn('[VideoStreamPlayer] Erro ao pausar player:', error);
    }
  }

  ngOnDestroy(): void {
    // Para o contador decrescente
    this.stopCountdownTimer();

    // Reset de estados
    this.timeRemainingSeconds = 0;
    this.playbackStarted = false;
    this.playbackStartTime = null;
    this.currentSession = null;
    this.currentStreamInfo = null;
    this.playerReady = false;
    this.isLoading = false;
    this.errorMessage = null;
    
    // Remove listener de fechamento da janela
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      console.info('[VideoStreamPlayer] Listener de fechamento da janela removido');
    }
    
    // Remove listener de mudança de visibilidade
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      console.info('[VideoStreamPlayer] Listener de mudança de visibilidade removido');
    }
    
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];

    this.srsStreamingService.stopStream();

    // Destroi player FLV se existir
    if (this.flvPlayer) {
      this.flvPlayer.destroy();
      this.flvPlayer = null;
    }
  }

  /**
   * Formata o tempo restante em formato mm:ss ou exibe 'Expirado' se o tempo for zero ou negativo.
   * @param timeRemaining Tempo restante em segundos
   * @returns string formatada
   */
  public formatTimeRemaining(timeRemaining: number): string {
    if (timeRemaining == null || isNaN(timeRemaining)) return '--:--';
    if (timeRemaining <= 0) return 'Expirado';
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }


} 